import type { WidgetConfig } from '@springboard/shared';
import { installGoalDetection } from './goals';
import { Tracker } from './tracker';
import { fnv1a, matchesAny, readCookie, storage, uuid, writeCookie } from './util';

/**
 * sb.js — the one-line embed (planning.md FR-W-01..05, FR-W-13..16).
 * Reads its own <script> tag, fetches the site's config, checks the origin, assigns the A/B bucket,
 * tracks page views and goals for BOTH arms, and lazy-loads sb.widget.js for the variation arm.
 *
 * Attributes: data-site-key (required), data-api (API origin; default: this script's origin),
 * data-config (explicit config URL, used by the console preview), data-preview (force open, no tracking).
 */
declare global {
  interface Window {
    springboard?: SpringboardApi;
    __sbWidget?: { mount: (opts: WidgetMountOptions) => WidgetHandle };
  }
}

export interface WidgetMountOptions {
  config: WidgetConfig;
  tracker: Tracker;
  autoOpen: boolean;
  delayMs: number;
  siteKey: string;
  /** API origin for search requests (FR-W-08). */
  apiBase: string;
  preview: boolean;
}
export interface WidgetHandle {
  open(): void;
  close(): void;
}
type SpringboardApi = ((cmd: string, ...args: unknown[]) => void) & { q?: unknown[][] };

const script = document.currentScript as HTMLScriptElement | null;

function boot(): void {
  if (!script) return;
  const siteKey = script.dataset.siteKey || '';
  if (!siteKey) return;
  const scriptUrl = new URL(script.src, location.href);
  const apiBase = (script.dataset.api || scriptUrl.origin).replace(/\/$/, '');
  const widgetUrl = scriptUrl.href.replace(/sb\.js(\?.*)?$/, 'sb.widget.js');
  const preview = script.dataset.preview !== undefined;
  const configUrl = script.dataset.config || `${apiBase}/v1/config/${encodeURIComponent(siteKey)}`;

  // Command queue so pages can call springboard('track', ...) before we are ready.
  const pending: unknown[][] = (window.springboard && window.springboard.q) || [];
  let handle: WidgetHandle | null = null;
  let tracker: Tracker | null = null;
  const api: SpringboardApi = (cmd, ...args) => {
    switch (cmd) {
      case 'track':
        if (args[0] === 'conversion' && tracker) tracker.conversion(String((args[1] as { goalId?: string } | undefined)?.goalId ?? 'custom'));
        break;
      case 'open':
        handle?.open();
        break;
      case 'close':
        handle?.close();
        break;
      case 'consent':
        if (tracker) tracker.enabled = !!(args[0] as { analytics?: boolean } | undefined)?.analytics && !preview;
        break;
      case 'identify':
        // Reserved for Phase 3 (planning.md §5.3). Intentionally a no-op in Phase 1.
        break;
    }
  };
  window.springboard = api;

  fetch(configUrl, { credentials: 'omit' })
    .then((r) => (r.ok ? (r.json() as Promise<WidgetConfig>) : Promise.reject(new Error(`config ${r.status}`))))
    .then(async (config) => {
      if (!preview && !matchesAny(location.origin, config.allowedOrigins)) return;

      const local = storage('local');
      const session = storage('session');

      const forced = preview;
      let vid = readCookie('sb_vid') || local?.getItem('sb_vid') || '';
      if (!vid) vid = uuid();
      writeCookie('sb_vid', vid, 365);
      local?.setItem('sb_vid', vid);
      let sid = session?.getItem('sb_sid') || '';
      const lastSeen = Number(session?.getItem('sb_sid_t') || 0);
      if (!sid || Date.now() - lastSeen > 30 * 60 * 1000) sid = uuid();
      session?.setItem('sb_sid', sid);
      session?.setItem('sb_sid_t', String(Date.now()));

      const exp = config.experiment;
      const billing = config.billing;
      const paid = billing?.status === 'active' || billing?.status === 'past_due';
      // FR-B-03: after the trial and its grace period, or once cancelled, the widget stops rendering.
      const trialOver = !!billing && billing.status === 'trial' && !!billing.graceEndsAt && Date.parse(billing.graceEndsAt) < Date.now();
      const billingOff = trialOver || billing?.status === 'cancelled' || billing?.status === 'disabled';
      // A running test past its planned end behaves as ended until the owner extends or pays (FR-B-03, D-27). Paid accounts keep serving.
      const expired = !paid && !!exp && exp.status === 'running' && !!exp.plannedEndAt && Date.parse(exp.plannedEndAt) < Date.now();
      let variant: 'control' | 'variation' = 'variation';
      if (billingOff && !forced) variant = 'control';
      else if (exp && !forced) {
        if (exp.status === 'productionised') variant = 'variation';
        else if (exp.status === 'ended' || expired) variant = 'control';
        else variant = fnv1a(`${vid}|${exp.id}`) % 100 < exp.split * 100 ? 'variation' : 'control';
      }
      if (!config.trigger.enabled && !forced) variant = 'control'; // master switch off

      tracker = new Tracker({ apiBase, siteKey, vid, sid, variant, experimentId: exp?.id, configVersion: config.version, enabled: !preview });
      tracker.track('page_view');
      installGoalDetection(config.goals, tracker);
      for (const call of pending) api(String(call[0]), ...call.slice(1));

      // Which document to render: the current config for the variation arm; for control, either nothing or
      // the pinned earlier version of a version-vs-version test (D-27).
      let renderConfig: WidgetConfig | null = variant === 'variation' ? config : null;
      if (variant === 'control' && !billingOff && exp && exp.status === 'running' && !expired && exp.controlVersion && config.trigger.enabled) {
        renderConfig = await fetch(`${apiBase}/v1/config/${encodeURIComponent(siteKey)}/${exp.controlVersion}`, { credentials: 'omit' })
          .then((r) => (r.ok ? (r.json() as Promise<WidgetConfig>) : null))
          .catch(() => null);
      }
      if (!renderConfig) return;

      const t = renderConfig.trigger;
      const path = location.pathname;
      const seenKey = `sb_seen_${siteKey}`;
      const seenAt = Number(local?.getItem(seenKey) || 0);
      const seenThisSession = session?.getItem(seenKey) === '1';
      const isMobile = matchMedia('(max-width: 640px)').matches;
      const excluded = matchesAny(path, t.excludePaths);
      // D-24: "first visit" expires after firstVisitExpiryHours; after that the visitor is new again.
      const withinExpiry = seenAt > 0 && Date.now() - seenAt < t.firstVisitExpiryHours * 3600e3;
      let autoOpen = !excluded && !seenThisSession && (isMobile ? t.mobile : true);
      if (t.showOn === 'never') autoOpen = false;
      else if (t.showOn === 'first_visit' && withinExpiry) autoOpen = false;
      if (forced) autoOpen = true;
      if (!autoOpen && !t.launcher) return;

      const mountOptions: WidgetMountOptions = { config: renderConfig, tracker, autoOpen, delayMs: forced ? 200 : t.delayMs, siteKey, apiBase, preview };
      const mount = () => {
        if (window.__sbWidget) handle = window.__sbWidget.mount(mountOptions);
      };
      if (window.__sbWidget) return mount();
      const s = document.createElement('script');
      s.src = widgetUrl;
      s.async = true;
      s.onload = mount;
      s.onerror = () => tracker?.track('error', { message: 'widget bundle failed to load' });
      document.head.appendChild(s);
    })
    .catch((err: unknown) => {
      // Never break the host page. A missing config (e.g. before the first publish) is silent.
      if (preview) console.warn('[springboard] ', err);
    });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();
